<section class="sales-page">
  <header class="page-heading">
    <div>
      <p>Point of sale</p>
      <h2>Sell Products</h2>
      <span
        >Create an internal customer sale and issue its invoice. Marketplace approval is not
        required for eligible business stock.</span
      >
    </div>
    <button
      class="secondary"
      type="button"
      [disabled]="isLoadingProducts"
      (click)="loadProducts()"
      title="Refresh products"
    >
      <span class="material-symbols-outlined">refresh</span>
      {{ isLoadingProducts ? 'Loading...' : 'Refresh' }}
    </button>
  </header>

  @if (successMessage) {
    <p class="state success">{{ successMessage }}</p>
  }
  @if (errorMessage) {
    <p class="state error">{{ errorMessage }}</p>
  }

  @if (completedOrder) {
    <section class="sale-complete">
      <span class="material-symbols-outlined">check_circle</span>
      <div>
        <strong>Sale completed</strong
        ><small
          >Order {{ completedOrder.id.slice(0, 8) }} |
          {{ formatAmount(completedOrder.totalAmount) }}</small
        >
      </div>
      <div class="invoice-actions">
        <button type="button" [disabled]="isOpeningInvoice" (click)="openInvoice()">
          <span class="material-symbols-outlined">picture_as_pdf</span>
          {{ isOpeningInvoice ? 'Opening...' : 'A4 invoice' }}
        </button>
        <button class="thermal-button" type="button" (click)="printThermalInvoice()">
          <span class="material-symbols-outlined">receipt_long</span>
          Thermal print
        </button>
      </div>
    </section>
  }

  <div class="sales-workspace">
    <section class="catalog-panel">
      <div class="panel-heading">
        <div>
          <h3>Internal sale inventory</h3>
          <span>{{ visibleProducts.length }} ready to sell in the business portal</span>
        </div>
        <label class="search-field">
          <span class="material-symbols-outlined">search</span>
          <input
            type="search"
            [(ngModel)]="search"
            placeholder="Search name, model or location"
            aria-label="Search products"
          />
        </label>
      </div>

      <div class="product-grid">
        @for (product of visibleProducts; track product.id) {
          <article class="product-card">
            <div class="product-image">
              @if (productImageUrl(product)) {
                <img [src]="productImageUrl(product)" [alt]="product.title" />
              } @else {
                <span class="material-symbols-outlined">inventory_2</span>
              }
            </div>
            <div class="product-copy">
              <strong>{{ product.title }}</strong>
              <span>{{ product.modelName || 'Model not specified' }}</span>
              <small>{{ product.availableQuantity || 0 }} in stock · {{ product.batchCount || 1 }} batch{{ (product.batchCount || 1) === 1 ? '' : 'es' }}</small>
              <small
                ><span class="material-symbols-outlined">location_on</span
                >{{ productLocation(product) }}</small
              >
            </div>
            <div class="product-action">
              <strong>Selling price {{ formatAmount(product.price) }}</strong>
              <button
                class="icon-button"
                type="button"
                title="Add product to sale"
                (click)="addToCart(product)"
              >
                <span class="material-symbols-outlined">add_shopping_cart</span>
              </button>
            </div>
          </article>
        } @empty {
          <div class="empty-state">
            <span class="material-symbols-outlined">inventory</span>
            <strong>{{
              isLoadingProducts ? 'Loading inventory...' : 'No inventory marked ready to sell'
            }}</strong>
            @if (!isLoadingProducts) { <small>Open Inventory and use “Mark ready to sell”. Marketplace publishing is not required.</small> }
          </div>
        }
      </div>
    </section>

    <aside class="checkout-panel">
      <div class="checkout-title">
        <div>
          <h3>Current sale</h3>
          <span>{{ totalQuantity }} item{{ totalQuantity === 1 ? '' : 's' }}</span>
        </div>
        <span class="material-symbols-outlined">shopping_cart</span>
      </div>

      <div class="cart-lines">
        @for (line of cart; track line.product.id) {
          <div class="cart-line">
            <div>
              <strong>{{ line.product.title }}</strong
              ><small>{{ line.product.availableQuantity || 0 }} available</small>
            </div>
            <label class="sale-price"><span>Sale price</span><input type="number" min="0.01" [ngModel]="line.unitPrice" (ngModelChange)="setUnitPrice(line, $event)" /></label>
            <div class="quantity-control">
              <button
                type="button"
                title="Decrease quantity"
                (click)="setQuantity(line, line.quantity - 1)"
              >
                <span class="material-symbols-outlined">remove</span>
              </button>
              <input
                type="number"
                min="1"
                max="99"
                [ngModel]="line.quantity"
                (ngModelChange)="setQuantity(line, $event)"
                [attr.aria-label]="'Quantity for ' + line.product.title"
              />
              <button
                type="button"
                title="Increase quantity"
                (click)="setQuantity(line, line.quantity + 1)"
              >
                <span class="material-symbols-outlined">add</span>
              </button>
              <button
                class="remove"
                type="button"
                title="Remove product"
                (click)="removeFromCart(line.product.id)"
              >
                <span class="material-symbols-outlined">close</span>
              </button>
            </div>
            <label class="sale-price discount-input"><span>Manual discount</span><input type="number" min="0" step="0.01" [ngModel]="line.discountAmount" (ngModelChange)="setDiscount(line, $event)" /></label>
          </div>
        } @empty {
          <p class="empty-cart">Add a product to begin this sale.</p>
        }
      </div>

      <section class="customer-section">
        <div class="section-label">
          <span>Customer</span><small>Registered account required</small>
        </div>
        @if (selectedCustomer) {
          <div class="selected-customer">
            <span class="material-symbols-outlined">account_circle</span>
            <div>
              <strong>{{ customerName(selectedCustomer) }}</strong
              ><small
                >{{ selectedCustomer.phoneNumber }} |
                {{ selectedCustomer.email || 'No email' }}</small
              >
            </div>
            <button type="button" title="Change customer" (click)="clearCustomer()">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
        } @else {
          @if (customers.length) {
            <select
              class="customer-select"
              [(ngModel)]="selectedCustomerId"
              (ngModelChange)="selectCustomer($event)"
              aria-label="Choose registered customer"
            >
              <option value="">Choose a registered customer</option>
              @for (customer of customers; track customer.identityUserId) {
                <option [value]="customer.identityUserId">
                  {{ customerName(customer) }} - {{ customer.phoneNumber }}
                </option>
              }
            </select>
          }
          <div class="customer-search">
            <span class="phone-prefix">+91</span>
            <input
              type="tel" inputmode="numeric" autocomplete="tel" maxlength="10" pattern="[0-9]{10}" [ngModel]="customerPhone" (ngModelChange)="customerPhoneInput($event)"
              placeholder="10-digit mobile"
              (keyup.enter)="lookupCustomer()"
              aria-label="Customer mobile number"
            />
            <button
              type="button"
              [disabled]="isLookingUpCustomer"
              (click)="lookupCustomer()"
              title="Find customer"
            >
              <span class="material-symbols-outlined">person_search</span>
              {{ isLookingUpCustomer ? 'Finding...' : 'Find' }}
            </button>
          </div>
          <button class="add-customer-toggle" type="button" (click)="toggleCreateCustomer()">
            <span class="material-symbols-outlined">{{
              showCreateCustomer ? 'close' : 'person_add'
            }}</span>
            {{ showCreateCustomer ? 'Cancel new customer' : 'Add new customer' }}
          </button>
          @if (showCreateCustomer) {
            <div class="new-customer-form">
              <div class="new-customer-heading">
                <span class="material-symbols-outlined">person_add</span>
                <div>
                  <strong>Add new customer</strong
                  ><small>The customer will be selected for this sale.</small>
                </div>
              </div>
              <div class="name-fields">
                <input
                  type="text"
                  maxlength="80"
                  [(ngModel)]="newCustomer.firstName"
                  placeholder="First name *"
                  aria-label="Customer first name"
                /><input
                  type="text"
                  maxlength="80"
                  [(ngModel)]="newCustomer.lastName"
                  placeholder="Last name"
                  aria-label="Customer last name"
                />
              </div>
              <input
                type="email"
                maxlength="160"
                [(ngModel)]="newCustomer.email"
                placeholder="Email address (optional)"
                aria-label="Customer email address"
              />
              <button type="button" [disabled]="isCreatingCustomer" (click)="createCustomer()">
                <span class="material-symbols-outlined">person_add</span
                >{{ isCreatingCustomer ? 'Adding customer...' : 'Add and select customer' }}
              </button>
            </div>
          }
        }
      </section>

      <section class="address-section">
        <div class="section-label">
          <span>Order fulfilment</span><small>Choose delivery or store pickup</small>
        </div>
        <div class="fulfilment-options">
          <button
            type="button"
            [class.active]="fulfillmentType === 'Pickup'"
            (click)="fulfillmentType = 'Pickup'"
          >
            <span class="material-symbols-outlined">storefront</span
            ><span><strong>Pickup</strong><small>Collect from store</small></span>
          </button>
          <button
            type="button"
            [class.active]="fulfillmentType === 'Delivery'"
            (click)="fulfillmentType = 'Delivery'"
          >
            <span class="material-symbols-outlined">local_shipping</span
            ><span><strong>Delivery</strong><small>Deliver to customer</small></span>
          </button>
        </div>
        @if (selectedCustomer) {
          <div class="fulfilment-customer">
            <span class="material-symbols-outlined">person</span>
            <div>
              <strong>{{ customerName(selectedCustomer) }}</strong
              ><small>{{ selectedCustomer.phoneNumber || customerPhone }}</small>
            </div>
          </div>
        }
        @if (fulfillmentType === 'Delivery') {
          <label
            >Delivery address <span>Required</span
            ><textarea
              rows="2"
              maxlength="350"
              [(ngModel)]="shippingAddress"
              placeholder="Enter complete customer delivery address"
            ></textarea>
          </label>
          <label class="check-row"
            ><input type="checkbox" [(ngModel)]="useShippingAsBilling" /> Use as billing
            address</label
          >
          @if (!useShippingAsBilling) {
            <label
              >Billing address <span>Optional</span
              ><textarea
                rows="2"
                [(ngModel)]="billingAddress"
                placeholder="Customer billing address"
              ></textarea>
            </label>
          }
        } @else {
          <div class="pickup-note">
            <span class="material-symbols-outlined">check_circle</span>Customer will collect this
            order from the store.
          </div>
        }
      </section>

      <section class="payment-section">
        <div class="section-label">
          <span>Payment</span><small>Cash, digital, credit, or split payment</small>
        </div>
        @for (payment of payments; track $index; let index = $index) {
          <div class="payment-line">
            <select [(ngModel)]="payment.method" aria-label="Payment method">
              @for (method of paymentMethods; track method.value) {
                <option [value]="method.value">{{ method.label }}</option>
              }
            </select>
            <div class="payment-amount">
              <span>₹</span><input type="number" min="0.01" step="0.01" [(ngModel)]="payment.amount" />
              <button type="button" (click)="fillPaymentBalance(index)">Balance</button>
            </div>
            @if (payment.method !== 'Cash' && payment.method !== 'Credit' && payment.method !== 'PayUQR') {
              <input [(ngModel)]="payment.transactionId" placeholder="Transaction ID (optional)" />
            }
            @if (payment.method === 'PayUQR') {
              <div class="payu-note"><span class="material-symbols-outlined">qr_code_2</span>QR will be created by PayU; payment remains pending until confirmed.</div>
            }
            @if (payments.length > 1) {
              <button class="remove-payment" type="button" (click)="removePayment(index)" aria-label="Remove payment">
                <span class="material-symbols-outlined">delete</span>
              </button>
            }
          </div>
        }
        @if (hasCreditPayment) {
          <div class="credit-details">
            <div class="credit-heading"><span class="material-symbols-outlined">schedule</span><div><strong>Credit balance {{ formatAmount(creditAmount) }}</strong><small>A customer receivable will be created automatically.</small></div></div>
            <label><span>Payment due date *</span><input type="date" [min]="today" [(ngModel)]="creditDueDate" required /></label>
            <label><span>Credit note (optional)</span><input [(ngModel)]="creditNotes" placeholder="Commitment or payment terms" /></label>
          </div>
        }
        <button class="add-payment" type="button" (click)="addPayment()">
          <span class="material-symbols-outlined">add</span>Add split payment
        </button>
        <div class="payment-summary" [class.unbalanced]="paymentBalance > 0">
          <span>Allocated {{ formatAmount(paymentTotal) }}</span>
          <strong>{{ paymentBalance > 0 ? 'Balance ' + formatAmount(paymentBalance) : 'Fully allocated' }}</strong>
        </div>
      </section>

      <div class="totals">
        <span
          >Subtotal <strong>{{ formatAmount(subtotal) }}</strong></span
        >
        <span class="grand-total"
          >Total <strong>{{ formatAmount(subtotal) }}</strong></span
        >
      </div>

      <button
        class="complete-button"
        type="button"
        [disabled]="!canCompleteSale"
        (click)="completeSale()"
      >
        <span class="material-symbols-outlined">point_of_sale</span>
        {{ isCompletingSale ? 'Completing sale...' : 'Complete sale' }}
      </button>
      @if (isCreatingPayUQr) {
        <div class="payu-result"><span class="spinner"></span>Creating secure PayU QR…</div>
      }
      @if (payUQr) {
        <div class="payu-result ready">
          <span class="material-symbols-outlined">verified</span>
          <div><strong>PayU payment ready</strong><small>Transaction {{ payUQr.transactionId }}</small></div>
          <a [href]="payUQr.qrString">Open UPI app</a>
        </div>
      }
    </aside>
  </div>
</section>
